/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { get, action, computed } from '@ember/object';
import { service } from '@ember/service';
import SubjectType from '@linc-technologies/linced-common/enums/subject-type';

// Dictionary mappings to improve lookup performance
function createIdLookup(items, idKey = 'id') {
  return Object.fromEntries(items.map((item) => [item[idKey], item]));
}

export default class TopicsOverviewController extends Controller {
  @service
  strings;

  tab = 'types';

  @computed('model.@each.isTopic')
  get topicSubjects() {
    return this.model.filter((subject) => {
      return (
        subject.isTopic &&
        (subject.visibleToTeachers ||
          subject.visibleToLearners ||
          subject.visibleToParents)
      );
    });
  }

  @computed('model.subjects.@each.{subjectTypeName,isVisible,isTopic}')
  get subjectsByType() {
    let subjects = this.get('model.subjects'); // assuming 'model' is an array of subject objects
    return subjects.reduce((result, subject) => {
      if (!subject.isTopic && subject.isVisible) {
        let type = subject.subjectTypeName;

        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(subject);
      }
      return result;
    }, {});
  }

  @computed('model.goals.@each.{category_id,subject_id,linkedGoals}')
  get subjectTypesArray() {
    let groupedData = {};
    const subjectsById = createIdLookup(this.model.subjects);
    // Loop through each goal in the model
    this.model.goals.forEach((goal) => {
      // Get the subject for the current goal
      const subjectId = get(goal, 'subject_id');
      const subject = subjectsById[goal.subject_id];

      if (subject && !groupedData[subjectId]) {
        let subjectType = subject.subjectTypeName;
        groupedData[subjectId] = {
          label: subject.label,
          type: subjectType,
        };
      }
    });
    return Object.entries(groupedData).map(([subjectId, data]) => ({
      subjectId,
      label: data.label,
      type: data.type,
    }));
  }

  @computed('model.goals.@each.{category_id,subject_id,linkedGoals}')
  get topicAndGoalCounts() {
    let groupedData = {};

    // Loop through each goal in the model
    this.model.goals.forEach((goal) => {
      // Get the subject for the current goal
      const subject = goal.subject;

      // Only proceed if the goal is a topic
      if (subject && subject.get('isTopic')) {
        const categoryId = goal.get('category_id');
        // Initialize the category in groupedData if it doesn't exist
        if (!groupedData[categoryId]) {
          groupedData[categoryId] = {
            topicCount: 0,
            subjects: {},
            types: {},
          };
        }

        // Increment the topic count
        groupedData[categoryId].topicCount += 1;

        // Loop through linked goals and count them by subject and subject type
        const linkedGoals = goal.get('linkedGoals') || [];
        linkedGoals.forEach((linkedGoal) => {
          const linkedSubject = linkedGoal.subject;

          if (linkedSubject && !linkedSubject.get('isTopic')) {
            let subjectType = linkedSubject.get('subjectTypeName');
            let subjectId = linkedSubject.get('id');
            // Initialize the subject type in the category if it doesn't exist
            if (!groupedData[categoryId].types[subjectType]) {
              groupedData[categoryId].types[subjectType] = 0;
            }
            // Increment the count for the subject type
            groupedData[categoryId].types[subjectType] += 1;

            if (!groupedData[categoryId].subjects[subjectId]) {
              groupedData[categoryId].subjects[subjectId] = {
                label: linkedSubject.get('label'),
                subjectType: linkedSubject.get('subjectTypeName'),
                count: 0,
              };
            }
            // Increment the count for the subject
            groupedData[categoryId].subjects[subjectId].count += 1;
          }
        });
      }
    });

    return Object.entries(groupedData).map(([categoryId, data]) => ({
      categoryId,
      topicCount: data.topicCount,
      subjects: data.subjects,
      types: data.types,
      colour: data.colour, // Include colour in the final array
    }));
  }

  @computed('model.goals.@each.{subject_id,linkedGoals}')
  get overviewCounts() {
    let overviewData = new Map();
    let units = [];
    let goals = new Map();
    this.model.goals.forEach((goal) => {
      let subject = goal.subject;
      if (!subject.get('visibleToTeachers')) {
        return;
      }
      if (subject.get('isTopic')) {
        units.push(goal);
      } else {
        if (!subject.get('visibleToTeachers')) {
          return;
        }
        if (overviewData.has(goal.subject_id)) {
          overviewData.get(goal.subject_id).totalGoals++;
        } else {
          let subjectType = subject.get('subjectTypeName');
          overviewData.set(goal.subject_id, {
            subjectId: goal.subject_id,
            label: subject.get('label'),
            type: subjectType,
            totalGoals: 1,
            linkedGoalIds: new Set(),
            linkedGoalsCount: 0,
            unassignedGoals: 0,
            sequence: subject.get('sequence'),
          });
        }
        goals.set(goal.id, {
          isTopic: goal.get('subject.isTopic'),
          linked: false,
          raw: goal,
          subject: goal.subject,
          subjectType: subject.get('subjectType'),
          visibleToTeachers: subject.get('visibleToTeachers'),
        });
      }
    });
    units.forEach((unit) => {
      (unit.linkedGoals || []).forEach((goal) => {
        if (!goals.has(goal.id)) {
          // Goal no longer exists?
          return;
        }
        goals.get(goal.id).linked = true;
        overviewData.get(goal.subject_id).linkedGoalIds.add(goal.id);
      });
    });
    overviewData.forEach((subject) => {
      subject.linkedGoalsCount = subject.linkedGoalIds.size;
      subject.unassignedGoals = subject.totalGoals - subject.linkedGoalsCount;
      subject.assignedPercentage =
        subject.totalGoals > 0
          ? ((subject.linkedGoalsCount / subject.totalGoals) * 100).toFixed(0)
          : 0;
      subject.unassignedPercentage =
        subject.totalGoals > 0
          ? ((subject.unassignedGoals / subject.totalGoals) * 100).toFixed(0)
          : 0;
    });

    // Convert to array format and add chartData
    const overviewArray = Array.from(overviewData.values())
      .map((data) => {
        // Generate chartData for each subject
        const subjectTypeInfo = SubjectType.values.find(
          (type) => type.label === data.type,
        ) || { colour: '#01667d' };
        // Generate chartData for each subject type
        const chartData = [
          {
            value: `Assigned`,
            count: data.linkedGoalsCount,
            tooltip: `Assigned goals: ${data.label}`,
            color: subjectTypeInfo.colour,
          },
          {
            value: `Unassigned`,
            count: data.unassignedGoals,
            tooltip: `Unassigned goals: ${data.label}`,
            color: '#CCCCCC',
          },
        ];

        return {
          subjectId: data.subjectId,
          subjectLabel: data.label,
          type: data.type,
          totalGoals: data.totalGoals,
          linkedGoalsCount: data.linkedGoalsCount,
          unassignedGoals: data.unassignedGoals,
          assignedPercentage: data.assignedPercentage,
          unassignedPercentage: data.unassignedPercentage,
          sequence: data.sequence,
          chartData,
        };
      })
      .sort((a, b) => a.type.localeCompare(b.type));

    return overviewArray;
  }

  isGroupingByLabel = true; // Default to grouping by label
  @tracked
  selectedSubject = null; // Stores the selected subject
  @tracked
  selectedType = null;

  @computed(
    'model.goals.@each.{subject_id,linkedGoals,category_id}',
    'model.subjects.@each.{isTopic,isVisible,subjectTypeName,label}',
    'model.categories.@each.{label,sequence}',
  )
  get overviewTypeCounts() {
    let overviewData = {};
    let unassignedByLabel = {};
    let unassignedBySubjectType = {};
    let assignedByLabel = {};
    let assignedBySubjectType = {};
    let allLinkedGoals = new Set();

    // Prepopulate all the subject types.
    SubjectType.values.forEach((subjectType) => {
      overviewData[subjectType.label] = {
        totalGoals: 0,
        linkedGoalsCount: 0,
        unassignedGoals: 0,
        linkedGoalIds: new Set(),
      };
    });

    // Loop through each goal in the model
    this.get('model.goals').forEach((goal) => {
      const subject = goal.get('subject');

      if (subject && subject.get('isVisible')) {
        if (subject.get('isTopic')) {
          // Add linked goals to the Set to count unique linked goals.
          const linkedGoals = get(goal, 'linkedGoals') || [];
          linkedGoals.forEach((linkedGoal) => {
            if (!linkedGoal.get('subject.isVisible')) {
              // Skip counting goals in hidden subjects, as they don't count
              // towards the total for the subject type.
              return;
            }
            const subjectType = linkedGoal.get('subject.subjectTypeName');
            overviewData[subjectType].linkedGoalIds.add(linkedGoal.id);
            allLinkedGoals.add(linkedGoal.id);
          });
        } else {
          // Increment total goals for the subject
          const subjectType = goal.get('subject.subjectTypeName');
          overviewData[subjectType].totalGoals += 1;
        }
      }
    });

    this.get('model.goals').forEach((goal) => {
      if (!goal.get('subject.isVisible') || goal.get('subject.isTopic')) {
        // Skip Topics and hidden Subjects.
        return;
      }
      // Group the goal based on whether it's assigned or unassigned
      let isAssigned = allLinkedGoals.has(goal.id);
      let targetByLabel = isAssigned ? assignedByLabel : unassignedByLabel;
      let targetBySubjectType = isAssigned
        ? assignedBySubjectType
        : unassignedBySubjectType;
      let subjectId = goal.subject_id;
      let subjectLabel = goal.get('subject.label');
      let subjectType = goal.get('subject.subjectTypeName');
      let category = goal.get('category');

      // Group by label and then by category within each label
      if (!targetByLabel[subjectId]) {
        targetByLabel[subjectId] = {
          label: subjectLabel,
          categories: {},
        };
      }
      let labelCategories = targetByLabel[subjectId].categories;
      if (category) {
        const categoryName = get(category, 'label');
        const categorySequence = get(category, 'sequence');
        if (!labelCategories[categoryName]) {
          labelCategories[categoryName] = {
            sequence: categorySequence,
            goals: [],
          };
        }
        labelCategories[categoryName].goals.push(goal);
      }

      // Group by subject type, then by subject, and by category within each label
      if (!targetBySubjectType[subjectType]) {
        targetBySubjectType[subjectType] = {};
      }
      if (!targetBySubjectType[subjectType][subjectId]) {
        targetBySubjectType[subjectType][subjectId] = {
          label: subjectLabel,
          categories: {},
        };
      }
      let typeCategories =
        targetBySubjectType[subjectType][subjectId].categories;
      if (category) {
        const categoryName = get(category, 'label');
        const categorySequence = get(category, 'sequence');
        if (!typeCategories[categoryName]) {
          typeCategories[categoryName] = {
            sequence: categorySequence,
            goals: [],
          };
        }
        typeCategories[categoryName].goals.push(goal);
      }
    });

    // Set linked goal counts for easy access.
    Object.keys(overviewData).forEach((subjectType) => {
      overviewData[subjectType].linkedGoalsCount =
        overviewData[subjectType].linkedGoalIds.size;
      overviewData[subjectType].unassignedGoals =
        overviewData[subjectType].totalGoals -
        overviewData[subjectType].linkedGoalsCount;
    });

    // Convert overviewData to array format for summary counts
    const overviewArray = Object.entries(overviewData).reduce(
      (overviews, [subjectType, data]) => {
        if (data.totalGoals === 0) {
          return overviews;
        }
        // Fetch the colour from the subjectTypes enum
        const subjectTypeInfo = SubjectType.values.find(
          (type) => type.label === subjectType,
        ) || { colour: '#01667d' }; // Default color if not found
        // Generate chartData for each subject type

        const chartData = [
          {
            value: `Assigned`,
            count: data.linkedGoalsCount,
            tooltip: `Assigned goals: ${data.linkedGoalsCount}`,
            color: subjectTypeInfo.colour, // Use the colour from the enum
          },
          {
            value: `Unassigned`,
            count: data.unassignedGoals,
            tooltip: `Unassigned goals: ${data.unassignedGoals}`,
            color: '#CCCCCC',
          },
        ];

        overviews.push({
          subjectType,
          totalGoals: data.totalGoals,
          linkedGoalsCount: data.linkedGoalsCount,
          unassignedGoals: data.unassignedGoals,
          assignedPercentage:
            data.totalGoals > 0
              ? ((data.linkedGoalsCount / data.totalGoals) * 100).toFixed(0)
              : 0,
          unassignedPercentage:
            data.totalGoals > 0
              ? ((data.unassignedGoals / data.totalGoals) * 100).toFixed(0)
              : 0,
          chartData, // Attach chartData here
        });
        return overviews;
      },
      [],
    );

    // Convert unassigned and assigned goals by label to array format, grouped and sorted by category sequence
    const unassignedGoalsBySubject = Object.entries(unassignedByLabel).map(
      ([subjectId, subject]) => ({
        subjectId,
        label: subject.label,
        categories: Object.entries(subject.categories)
          .map(([categoryName, { sequence, goals }]) => ({
            categoryName,
            sequence,
            goals,
          }))
          .sort((a, b) => a.sequence - b.sequence),
      }),
    );

    const assignedGoalsBySubject = Object.entries(assignedByLabel).map(
      ([subjectId, subject]) => ({
        subjectId,
        label: subject.label,
        categories: Object.entries(subject.categories)
          .map(([categoryName, { sequence, goals }]) => ({
            categoryName,
            sequence,
            goals,
          }))
          .sort((a, b) => a.sequence - b.sequence),
      }),
    );

    // Convert unassigned and assigned goals by subject type to array format, grouped and sorted by subject and category sequence
    const unassignedGoalsBySubjectType = Object.entries(
      unassignedBySubjectType,
    ).map(([typeName, subjects]) => ({
      typeName,
      subjects: Object.entries(subjects).map(([subjectId, subject]) => ({
        subjectId,
        subjectLabel: subject.label,
        categories: Object.entries(subject.categories)
          .map(([categoryName, { sequence, goals }]) => ({
            categoryName,
            sequence,
            goals,
          }))
          .sort((a, b) => a.sequence - b.sequence),
      })),
    }));

    const assignedGoalsBySubjectType = Object.entries(
      assignedBySubjectType,
    ).map(([typeName, subjects]) => ({
      typeName,
      subjects: Object.entries(subjects).map(([subjectId, subject]) => ({
        subjectId,
        subjectLabel: subject.label,
        categories: Object.entries(subject.categories)
          .map(([categoryName, { sequence, goals }]) => ({
            categoryName,
            sequence,
            goals,
          }))
          .sort((a, b) => a.sequence - b.sequence),
      })),
    }));

    // Return the data
    return {
      overviewArray,
      unassignedGoalsBySubject,
      unassignedGoalsBySubjectType,
      assignedGoalsBySubject,
      assignedGoalsBySubjectType,
    };
  }

  @tracked
  unassignedDialogOpen = false;
  @tracked
  assignedDialogOpen = false;

  @action
  showUnassignedSubjectList(subjectId) {
    this.selectedSubject = subjectId;
    this.selectedType = null;
    this.unassignedDialogOpen = true;
    this.assignedDialogOpen = false;
  }

  @action
  showUnassignedTypeList(type) {
    this.selectedType = type;
    this.selectedSubject = null;
    this.unassignedDialogOpen = true;
    this.assignedDialogOpen = false;
  }

  @action
  showAssignedSubjectList(subjectId) {
    this.selectedSubject = subjectId;
    this.selectedType = null;
    this.unassignedDialogOpen = false;
    this.assignedDialogOpen = true;
  }

  @action
  showAssignedTypeList(type) {
    this.selectedType = type;
    this.selectedSubject = null;
    this.unassignedDialogOpen = false;
    this.assignedDialogOpen = true;
  }

  @action
  showSubjectList(subjectId, slice) {
    if (slice.value === 'Assigned') {
      this.showAssignedSubjectList(subjectId);
    } else {
      this.showUnassignedSubjectList(subjectId);
    }
  }

  @action
  showTypeList(type, slice) {
    if (slice.value === 'Assigned') {
      this.showAssignedTypeList(type);
    } else {
      this.showUnassignedTypeList(type);
    }
  }
}
